import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Seo, breadcrumbSchema, serviceSchema, faqSchema } from "@/components/seo/Seo";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { Eyebrow, Reveal } from "@/components/site/Reveal";
import { useMedia } from "@/context/MediaContext";
import { MediaFrame } from "@/components/media/MediaFrame";
import { ALL_LANDINGS } from "@/content/landings";
import PersonalCTA from "@/components/home/PersonalCTA";
import Contact from "@/components/site/Contact";

const scrollToContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

const RelatedList = ({ label, paths }) => (
  <div>
    <p className="label-eyebrow text-[#2E1B16]/45">{label}</p>
    <ul className="mt-4 divide-y divide-[#2E1B16]/10">
      {paths.map((p) => {
        const page = ALL_LANDINGS.find((l) => l.path === p);
        if (!page) return null;
        return (
          <li key={p}>
            <Link to={p} data-testid={`related-${page.key}`} className="group flex items-center justify-between py-3 font-semibold text-[#2E1B16] hover:text-[#38BDF8] transition-colors">
              {page.nav} <ArrowUpRight size={16} className="text-[#2E1B16]/30 group-hover:text-[#38BDF8] -rotate-45 group-hover:rotate-0 transition-all" />
            </Link>
          </li>
        );
      })}
    </ul>
  </div>
);

// Shared editorial template for the service and industry SEO pages. Content lives in content/landings.js.
const LandingPage = ({ page, kind }) => {
  const { items } = useMedia(page.slot, 0);
  const parent = kind === "service" ? { name: "Services", to: "/services" } : { name: "Industries", to: "/industries" };
  const crumbs = [{ name: "Home", to: "/" }, parent, { name: page.nav, to: page.path }];
  const schema = [breadcrumbSchema(crumbs), faqSchema(page.faqs)];
  if (kind === "service") schema.push(serviceSchema({ name: page.nav, description: page.description, path: page.path }));

  return (
    <article data-testid={`landing-${page.key}`}>
      <Seo title={page.title} description={page.description} schema={schema} />

      <header className="pt-32 md:pt-44 pb-16 md:pb-24 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
          <Breadcrumbs items={crumbs} />
          <p className="mt-10 label-eyebrow text-[#38BDF8]">{page.eyebrow}</p>
          <h1 className="mt-6 font-display uppercase text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[0.95] text-[#2E1B16] max-w-5xl">
            {page.h1[0]} <span className="font-accent font-normal normal-case text-[#38BDF8] text-[1.1em]">{page.h1[1]}</span>
          </h1>
        </motion.div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-8 pb-24 md:pb-32">
        <div className={`space-y-6 text-lg md:text-xl text-[#2E1B16]/75 leading-relaxed ${items[0] ? "lg:col-span-6" : "lg:col-span-8"}`}>
          {page.intro.map((p, i) => <Reveal key={i} delay={i * 0.05}><p className={i === 0 ? "font-accent text-2xl md:text-3xl text-[#2E1B16] leading-snug" : ""}>{p}</p></Reveal>)}
          <Reveal delay={0.1}>
            <button onClick={scrollToContact} data-testid="landing-cta-top" className="group mt-4 inline-flex items-center gap-2 rounded-full bg-[#2E1B16] text-[#FAF7F2] font-semibold px-7 py-4 hover:bg-[#38BDF8] hover:text-[#2E1B16] transition-colors">
              Tell us about your business <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
        {items[0] && (
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal><MediaFrame item={items[0]} ratio="aspect-[4/5]" autoplay hoverPlay={false} className="rounded-sm" sizes="(min-width:1024px) 40vw, 100vw" /></Reveal>
          </div>
        )}
      </section>

      <section className="bg-[#F2EFE9] py-24 md:py-32" aria-labelledby="who-heading">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-4"><h2 id="who-heading" className="font-display uppercase text-3xl md:text-4xl tracking-tight text-[#2E1B16]">{page.who.heading}</h2></Reveal>
          <ul className="lg:col-span-7 lg:col-start-6 divide-y divide-[#2E1B16]/10" data-testid="landing-who">
            {page.who.items.map((it, i) => (
              <Reveal key={it} delay={i * 0.04}><li className="py-4 flex gap-5 text-lg text-[#2E1B16]/80"><span className="text-xs font-semibold text-[#38BDF8] pt-1.5 tabular-nums">{String(i + 1).padStart(2, "0")}</span>{it}</li></Reveal>
            ))}
          </ul>
        </div>
      </section>

      {page.problems && (
        <section className="py-20 md:py-28" aria-labelledby="problems-heading">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
            <Reveal className="lg:col-span-4"><h2 id="problems-heading" className="font-display uppercase text-3xl md:text-4xl tracking-tight text-[#2E1B16]">{page.problems.heading}</h2></Reveal>
            <ul className="lg:col-span-7 lg:col-start-6 divide-y divide-[#2E1B16]/10" data-testid="landing-problems">
              {page.problems.items.map((it, i) => (
                <Reveal key={it} delay={i * 0.04}><li className="py-4 flex gap-5 text-lg text-[#2E1B16]/80"><span className="text-[#38BDF8] pt-1.5" aria-hidden="true">—</span>{it}</li></Reveal>
              ))}
            </ul>
          </div>
        </section>
      )}

      {page.customersCare && (
        <section className="bg-[#F2EFE9] py-20 md:py-28" aria-labelledby="care-heading">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
            <Reveal className="lg:col-span-4"><h2 id="care-heading" className="font-display uppercase text-3xl md:text-4xl tracking-tight text-[#2E1B16]">{page.customersCare.heading}</h2></Reveal>
            <ul className="lg:col-span-7 lg:col-start-6 divide-y divide-[#2E1B16]/10" data-testid="landing-care">
              {page.customersCare.items.map((it, i) => (
                <Reveal key={it} delay={i * 0.04}><li className="py-4 flex gap-5 text-lg text-[#2E1B16]/80"><span className="text-xs font-semibold text-[#38BDF8] pt-1.5 tabular-nums">{String(i + 1).padStart(2, "0")}</span>{it}</li></Reveal>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="py-24 md:py-32" aria-labelledby="how-heading">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Eyebrow>How it works</Eyebrow></Reveal>
          <Reveal delay={0.05}><h2 id="how-heading" className="mt-6 font-display uppercase text-3xl md:text-5xl tracking-tight text-[#2E1B16] max-w-3xl">{page.how.heading}</h2></Reveal>
          <ol className="mt-16 grid md:grid-cols-2 gap-x-16 gap-y-14" data-testid="landing-how">
            {page.how.steps.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <li className="border-t border-[#2E1B16]/12 pt-6">
                  <span className="font-display text-5xl text-[#2E1B16]/10">0{i + 1}</span>
                  <h3 className="mt-2 font-display text-2xl tracking-tight text-[#2E1B16]">{t}</h3>
                  <p className="mt-3 text-lg text-[#2E1B16]/65 leading-relaxed">{d}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-[#2E1B16] text-[#FAF7F2] py-24 md:py-32" aria-labelledby="get-heading">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal><h2 id="get-heading" className="font-display uppercase text-3xl md:text-5xl tracking-tight">{page.get.heading}</h2></Reveal>
            <div className="mt-10 space-y-5 text-lg text-[#FAF7F2]/70 leading-relaxed">
              {page.why.map((p, i) => <Reveal key={i} delay={0.05 + i * 0.05}><p>{p}</p></Reveal>)}
            </div>
          </div>
          <ul className="lg:col-span-6 lg:col-start-7 grid sm:grid-cols-2 gap-x-10" data-testid="landing-get">
            {page.get.items.map((it, i) => (
              <Reveal key={it} delay={i * 0.03}><li className="border-t border-[#FAF7F2]/12 py-4 text-lg">{it}</li></Reveal>
            ))}
          </ul>
        </div>
      </section>

      {page.contentIdeas && (
        <section className="py-20 md:py-28" aria-labelledby="ideas-heading">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal><Eyebrow>Ideas</Eyebrow></Reveal>
            <Reveal delay={0.05}><h2 id="ideas-heading" className="mt-6 font-display uppercase text-3xl md:text-4xl tracking-tight text-[#2E1B16] max-w-3xl">{page.contentIdeas.heading}</h2></Reveal>
            <ul className="mt-10 flex flex-wrap gap-3" data-testid="landing-ideas">
              {page.contentIdeas.items.map((it, i) => (
                <Reveal key={it} delay={i * 0.03}><li className="rounded-full border border-[#2E1B16]/15 px-5 py-2.5 text-base text-[#2E1B16]/80">{it}</li></Reveal>
              ))}
            </ul>
          </div>
        </section>
      )}

      {page.caseStudy && (
        <section className="py-10 md:py-16" aria-labelledby="case-link-heading">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <Link to={page.caseStudy.to} data-testid="landing-case-study" className="group block rounded-sm border border-[#2E1B16]/15 p-8 md:p-12 hover:border-[#38BDF8] transition-colors">
                <p className="label-eyebrow text-[#38BDF8]">Case study</p>
                <h2 id="case-link-heading" className="mt-4 font-display uppercase text-3xl md:text-4xl tracking-tight text-[#2E1B16] group-hover:text-[#38BDF8] transition-colors">{page.caseStudy.label}</h2>
                <p className="mt-4 text-lg text-[#2E1B16]/70 leading-relaxed max-w-2xl">{page.caseStudy.blurb}</p>
                <span className="mt-6 inline-flex items-center gap-2 font-semibold text-[#2E1B16] group-hover:text-[#38BDF8] transition-colors">Read the full story <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></span>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      <section className="py-24 md:py-32" aria-labelledby="faq-heading">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal><h2 id="faq-heading" className="font-display uppercase text-3xl md:text-4xl tracking-tight text-[#2E1B16]">Questions we get asked</h2></Reveal>
            <div className="mt-12 space-y-10">
              <RelatedList label={kind === "service" ? "Industries this suits" : "Services for this industry"} paths={kind === "service" ? page.related.industries : page.related.services} />
              <RelatedList label={kind === "service" ? "Related services" : "Related industries"} paths={kind === "service" ? page.related.services : page.related.industries} />
              <Link to="/work" data-testid="landing-work-link" className="inline-flex items-center gap-2 font-semibold text-[#2E1B16] hover:text-[#38BDF8] transition-colors">See the work <ArrowRight size={16} /></Link>
            </div>
          </div>
          <dl className="lg:col-span-7 lg:col-start-6 divide-y divide-[#2E1B16]/10" data-testid="landing-faqs">
            {page.faqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.04}>
                <div className="py-7">
                  <dt className="font-display text-xl md:text-2xl tracking-tight text-[#2E1B16]">{f.q}</dt>
                  <dd className="mt-3 text-lg text-[#2E1B16]/65 leading-relaxed">{f.a}</dd>
                </div>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>

      <PersonalCTA />
      <Contact pageLabel={page.nav} />
    </article>
  );
};

export default LandingPage;
